import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface CallLogEntry {
  id: string;
  brand_id: string;
  twilio_call_sid: string | null;
  twilio_number_id: string | null;
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  status: string;
  duration_seconds: number | null;
  voicemail_url: string | null;
  voicemail_sid: string | null;
  user_id: string | null;
  contact_id: string | null;
  lead_id: string | null;
  account_id: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
  // Joined
  contact?: { id: string; first_name: string | null; last_name: string | null } | null;
  lead?: { id: string; name: string | null } | null;
  user?: { id: string; full_name: string | null } | null;
}

/**
 * Fetch call_log entries for a specific lead or account (used by ContactContextPanelV2).
 * Matches on lead_id OR account_id, ordered newest-first.
 */
export function useCallLogForContact({
  leadId,
  accountId,
  limit = 30,
}: {
  leadId: string | null;
  accountId: string | null;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['call-log', 'contact', leadId, accountId, limit],
    queryFn: async () => {
      if (!leadId && !accountId) return [] as CallLogEntry[];

      let query = supabase
        .from('call_log')
        .select(`
          *,
          contact:contacts!call_log_contact_id_fkey(id, first_name, last_name),
          lead:leads!call_log_lead_id_fkey(id, name),
          user:users!call_log_user_id_fkey(id, full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (leadId && accountId) {
        query = query.or(`lead_id.eq.${leadId},account_id.eq.${accountId}`);
      } else if (leadId) {
        query = query.eq('lead_id', leadId);
      } else if (accountId) {
        query = query.eq('account_id', accountId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as CallLogEntry[];
    },
    enabled: !!(leadId || accountId),
    staleTime: 15_000,
  });
}

export function useCallLog(limit = 50) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['call-log', user?.id, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('call_log')
        .select(`
          *,
          contact:contacts!call_log_contact_id_fkey(id, first_name, last_name),
          lead:leads!call_log_lead_id_fkey(id, name),
          user:users!call_log_user_id_fkey(id, full_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as CallLogEntry[];
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  });
}
